import React from 'react'
import Router from 'next/router'
import { ApolloError } from '@apollo/client'
import { CheckAuthDocument } from '@app/lib/graphql'
import { NextPage } from 'next'
import { ApolloPageContext } from 'next-with-apollo'

const AUTH_CHECK_RETRIES = 3
const AUTH_CHECK_RETRY_DELAY_MS = 300
const UNKNOWN_TENANT_CODE = 'ERR_UNKNOWN_TENANT'

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const isNetworkError = (error: unknown) =>
	error instanceof ApolloError && !!error.networkError

export function hasUnknownTenantError(graphQLErrors?: any, networkError?: any) {
	return [
		...(graphQLErrors ?? []),
		...(networkError?.result?.errors ?? []),
	].some((e) => e?.extensions?.code === UNKNOWN_TENANT_CODE)
}

const isUnknownTenantError = (error: unknown) =>
	error instanceof ApolloError &&
	hasUnknownTenantError(error.graphQLErrors, error.networkError)

const redirectToLogin = (ctx: ApolloPageContext) => {
	if (ctx.res) {
		ctx.res.writeHead(302, {
			Location: '/login',
		})
		ctx.res.end()
	} else {
		Router.push('/login')
	}
}

async function queryCheckAuth(apolloClient: ApolloPageContext['apolloClient']) {
	return apolloClient.query({ query: CheckAuthDocument })
}

export async function authCheck(ctx: ApolloPageContext) {
	if (
		process.env.NODE_ENV !== 'development' &&
		ctx.pathname !== '/login' &&
		!ctx.pathname.startsWith('/verify-email') &&
		!ctx.pathname.startsWith('/auth/') &&
		ctx.pathname !== '/healthcheck'
	) {
		let lastError: unknown

		for (let attempt = 1; attempt <= AUTH_CHECK_RETRIES; attempt++) {
			try {
				const { data } = await queryCheckAuth(ctx.apolloClient)

				if (!data?.checkAuth) {
					redirectToLogin(ctx)
					return false
				}

				return true
			} catch (error) {
				lastError = error

				if (isUnknownTenantError(error)) {
					redirectToLogin(ctx)
					return false
				}

				if (!isNetworkError(error) || attempt === AUTH_CHECK_RETRIES) {
					break
				}

				await sleep(AUTH_CHECK_RETRY_DELAY_MS * attempt)
			}
		}

		console.error('Auth check failed after retries:', lastError)
		return true
	}

	return true
}

export function withAuth<TPageProps = {}>(Page: NextPage<TPageProps>) {
	const withAuth: NextPage<TPageProps> = (props: TPageProps) => (
		// @ts-ignore
		<Page {...props} />
	)

	withAuth.getInitialProps = async (ctx: ApolloPageContext) => {
		await authCheck(ctx)

		return {} as TPageProps
	}

	if (Page.getInitialProps) {
		withAuth.getInitialProps = Page.getInitialProps
	}

	withAuth.displayName = `withAuth(${Page.displayName})`

	return withAuth
}
